import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth/useAuth';
import { isSafeRedirect } from '../../lib/routing';

// Routes that an authenticated-but-unverified user is permitted to visit without
// being bounced to /auth/verify-email-pending.
const VERIFY_EMAIL_ROUTES = ['/auth/verify-email-pending', '/auth/verify-email'];

interface RouteGuardProps {
  children: React.ReactNode;
  /**
   * If true, redirects authenticated users away from this route (e.g., login page).
   * If false, redirects unauthenticated users to login (protected routes).
   */
  requireGuest?: boolean;
  /**
   * If true, requires the user to have an accountId.
   * Redirects authenticated users without an account to /select-account.
   */
  requireAccount?: boolean;
  /**
   * If true, requires only that the user is authenticated. Unlike the default
   * authenticated mode, this variant does NOT redirect accountless users to
   * /select-account: they are allowed through.
   *
   * Use for routes that are meaningful to authenticated users regardless of
   * whether they have an account yet (e.g. /auth/verify-email-pending, which is
   * only reachable immediately after registration before an account exists).
   */
  requireAuthOnly?: boolean;
}

/**
 * RouteGuard prevents route flickering by blocking render until auth state is known.
 *
 * Usage:
 * - Protected routes: <RouteGuard requireAccount><Dashboard /></RouteGuard>
 * - Guest-only routes: <RouteGuard requireGuest><Login /></RouteGuard>
 * - Onboarding: <RouteGuard><Onboarding /></RouteGuard> (authenticated but no account required)
 * - Verify-email pending: <RouteGuard requireGuest><VerifyEmailPending /></RouteGuard>
 *   (unauthenticated: register no longer mints a JWT, so the pending page is a guest page)
 *
 * This component establishes the pattern for auth-aware routing:
 * 1. Never render content while auth is initializing
 * 2. Use Navigate with replace to avoid history pollution
 * 3. Show consistent loading states across the app
 */
export function RouteGuard({ children, requireGuest = false, requireAccount = false, requireAuthOnly = false }: RouteGuardProps) {
  const { user, isAuthenticated, accountId, isInitializing } = useAuth();
  const location = useLocation();

  // Block render until we know auth state
  if (isInitializing) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="text-sm text-text-muted">Loading...</div>
      </div>
    );
  }

  // Redirect authenticated users away from guest-only pages (login/register).
  // The destination depends on the user's onboarding state and any safe
  // `?redirect=` param on the URL:
  //   - email not yet verified -> /auth/verify-email-pending always wins
  //     (verification is the gate before anything else; just-registered users
  //     never see the inbox prompt otherwise).
  //   - safe redirect present -> redirect target. Honoured for the existing-user
  //     invite-with-password convergence path (/login?redirect=/invite/<token>)
  //     and for any other deep-link-then-sign-in flow. Resolving the redirect
  //     here closes a same-render-batch race where callers like login.tsx's
  //     handleSigninSuccess and this guard would both fire navigates after
  //     auth.login() flips isAuthenticated.
  //   - has accountId -> /dashboard
  //   - no accountId -> /select-account
  if (requireGuest && isAuthenticated) {
    if (user?.emailVerified == null) {
      return <Navigate to="/auth/verify-email-pending" replace />;
    }
    const redirect = new URLSearchParams(location.search).get('redirect');
    if (isSafeRedirect(redirect)) {
      return <Navigate to={redirect} replace />;
    }
    if (accountId) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/select-account" replace />;
  }

  // Redirect unauthenticated users to login from protected pages,
  // preserving the original URL so they return here after login.
  if (!requireGuest && !isAuthenticated) {
    const currentPath = location.pathname + location.search + location.hash;
    const loginPath = currentPath && currentPath !== '/'
      ? `/login?redirect=${encodeURIComponent(currentPath)}`
      : '/login';
    return <Navigate to={loginPath} replace />;
  }

  // Run after auth-init, after the requireGuest short-circuit (which handles login/signup pages),
  // and after the unauth-bounce. Skipping the redirect on the verify routes themselves is required
  // so the unverified user can see the pending page and click the verify link.
  if (isAuthenticated && user?.emailVerified == null && !VERIFY_EMAIL_ROUTES.includes(location.pathname)) {
    return <Navigate to="/auth/verify-email-pending" replace />;
  }

  // requireAuthOnly: authenticated users pass through regardless of account state.
  // Skip the no-account -> /select-account redirect for these routes.
  if (requireAuthOnly) {
    return <>{children}</>;
  }

  // Redirect authenticated users without account to select-account (for requireAccount routes)
  if (requireAccount && isAuthenticated && !accountId) {
    const currentPath = location.pathname + location.search + location.hash;
    return <Navigate to={`/select-account?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }

  return <>{children}</>;
}
